import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  FormControl,
  FormLabel,
  Input,
  Button,
  Select,
  Grid,
  Text,
  Stack,
  Divider,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { FaBriefcase } from 'react-icons/fa';
import PageHero from '../Components/PageHero';
import useSubmitApplication from '../Components/useSubmitApplication';
import { fetchJobs, pickLocalized } from '../lib/content';

const ApplicationForm = () => {
  const { t, i18n } = useTranslation();
  const { jobId } = useParams();
  const { submitApplication, loading } = useSubmitApplication();
  const [job, setJob] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    place: '',
    nationality: '',
    sex: '',
    cv: null,
    cover_letter: null,
    other_documents: null,
    years_of_experience: '',
    starting_date: '',
  });

  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    fetchJobs().then(({ data }) => {
      if (cancelled) return;
      const match = (data || []).find((item) => String(item.id) === String(jobId));
      if (match) setJob(match);
    });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await submitApplication({
        ...formData,
        job_id: jobId || '',
        job_title: job?.title || '',
      });
      setFormData({
        full_name: '',
        email: '',
        place: '',
        nationality: '',
        sex: '',
        cv: null,
        cover_letter: null,
        other_documents: null,
        years_of_experience: '',
        starting_date: '',
      });
      e.target.reset();
    } catch (error) {
      // The hook already displays the error toast.
    }
  };

  return (
    <Box pb={16}>
      <PageHero title={t('application.title')} />
      <Box as="section" py={{ base: 14, md: 20 }} bg="surface.muted">
        <Container maxW="5xl">
          <Box
            bg="surface.base"
            borderRadius="2xl"
            boxShadow="sm"
            border="1px solid"
            borderColor="border.subtle"
            p={{ base: 6, md: 12 }}
          >
            {job && (
              <Flex
                align="center"
                gap={3}
                bg="brand.50"
                borderRadius="xl"
                px={5}
                py={4}
                mb={6}
              >
                <Icon as={FaBriefcase} color="brand.600" />
                <Text fontWeight="600">
                  {t('application.forJob')} {pickLocalized(job.title, i18n.language)}
                </Text>
              </Flex>
            )}
            <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="700" mb={2} textAlign="center">
              {t('application.title')}
            </Text>
            <Divider mb={8} />
            <form onSubmit={handleSubmit}>
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
                <FormControl isRequired>
                  <FormLabel htmlFor="full_name" fontWeight="600">{t('application.fullName')}</FormLabel>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder={t('application.fullName')}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel htmlFor="email" fontWeight="600">{t('application.email')}</FormLabel>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('application.email')}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel htmlFor="place" fontWeight="600">{t('application.place')}</FormLabel>
                  <Input
                    id="place"
                    name="place"
                    value={formData.place}
                    onChange={handleChange}
                    placeholder={t('application.placePlaceholder')}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel htmlFor="nationality" fontWeight="600">{t('application.nationality')}</FormLabel>
                  <Select
                    id="nationality"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    placeholder={t('application.nationalityPlaceholder')}
                  >
                    <option value="congolese">{t('application.nationalityCongolese')}</option>
                    <option value="kenyan">{t('application.nationalityKenyan')}</option>
                    <option value="rwandan">{t('application.nationalityRwandan')}</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel htmlFor="sex" fontWeight="600">{t('application.sex')}</FormLabel>
                  <Select
                    id="sex"
                    name="sex"
                    value={formData.sex}
                    onChange={handleChange}
                    placeholder={t('application.sexPlaceholder')}
                  >
                    <option value="male">{t('application.sexMale')}</option>
                    <option value="female">{t('application.sexFemale')}</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel htmlFor="cv" fontWeight="600">{t('application.cv')}</FormLabel>
                  <Input
                    id="cv"
                    name="cv"
                    type="file"
                    onChange={handleFileChange}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel htmlFor="cover_letter" fontWeight="600">{t('application.coverLetter')}</FormLabel>
                  <Input
                    id="cover_letter"
                    name="cover_letter"
                    type="file"
                    onChange={handleFileChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="other_documents" fontWeight="600">{t('application.otherDocuments')}</FormLabel>
                  <Input
                    id="other_documents"
                    name="other_documents"
                    type="file"
                    onChange={handleFileChange}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel htmlFor="years_of_experience" fontWeight="600">{t('application.experience')}</FormLabel>
                  <Select
                    id="years_of_experience"
                    name="years_of_experience"
                    value={formData.years_of_experience}
                    onChange={handleChange}
                    placeholder={t('application.experiencePlaceholder')}
                  >
                    <option value="beginner">{t('application.experienceBeginner')}</option>
                    <option value="intermediate">{t('application.experienceIntermediate')}</option>
                    <option value="advanced">{t('application.experienceAdvanced')}</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel htmlFor="starting_date" fontWeight="600">{t('application.startDate')}</FormLabel>
                  <Input
                    id="starting_date"
                    name="starting_date"
                    type="date"
                    value={formData.starting_date}
                    onChange={handleChange}
                  />
                </FormControl>
              </Grid>
              <Stack direction={{ base: 'column', md: 'row' }} justifyContent="center" mt={8} spacing={4}>
                <Button size="lg" colorScheme="brand" isLoading={loading} type="submit" loadingText={t('application.submit')}>
                  {t('application.submit')}
                </Button>
              </Stack>
            </form>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ApplicationForm;